import { NavigateFunction } from 'react-router-dom';
import { ROUTES } from '@/constants';
import { SUPABASE_PROJECT_ID } from '@/config/supabase';

const RETURN_TO_KEY = 'auth_return_to';
const OAUTH_LOCALE_KEY = 'oauth_locale';
const SUPABASE_STORAGE_KEY = `sb-${SUPABASE_PROJECT_ID}-auth-token`;
const RETURNING_VISITOR_KEY = 'has_authenticated';

/**
 * ============================================
 * SMART AUTH MODE DETECTION - SOURCE OF TRUTH
 * ============================================
 * All auth redirects MUST use these helpers to determine
 * whether to show signin or signup based on returning visitor status.
 * 
 * ESLint rule: no-hardcoded-auth-mode enforces this pattern.
 */

/**
 * Check if this browser has ever had a successful authentication.
 * This persists even after logout (unlike the session token).
 * Used to determine default auth mode (signin vs signup).
 */
export const isReturningVisitor = (): boolean => {
  return localStorage.getItem(RETURNING_VISITOR_KEY) === 'true';
};

/**
 * Mark this browser as having authenticated.
 * Call this after successful login/signup.
 */
export const markAsAuthenticated = (): void => {
  try {
    localStorage.setItem(RETURNING_VISITOR_KEY, 'true');
  } catch {
    // Safari iframe may block localStorage — non-critical, skip silently
  }
};

/**
 * Get the smart auth mode for embedded forms.
 * Returns 'signin' for returning visitors, 'signup' for new visitors.
 */
export const getSmartAuthMode = (): 'signin' | 'signup' => {
  return isReturningVisitor() ? 'signin' : 'signup';
};

/**
 * Get the smart auth route based on returning visitor status.
 * Returns signin route for returning visitors, signup route for new visitors.
 * @param returnTo Optional path to redirect to after auth
 */
export const getSmartAuthRoute = (returnTo?: string): string => {
  const isReturning = isReturningVisitor();
  const baseRoute = isReturning ? ROUTES.AUTH : ROUTES.AUTH_SIGNUP;
  
  if (returnTo) {
    const separator = baseRoute.includes('?') ? '&' : '?';
    return `${baseRoute}${separator}returnTo=${encodeURIComponent(returnTo)}`;
  }
  return baseRoute;
};

/**
 * Fast synchronous check for existing session in localStorage.
 * Use this to redirect logged-in users BEFORE waiting for Supabase API.
 * 
 * @returns true if a valid (non-expired) session exists in localStorage
 */
export const hasStoredSession = (): boolean => {
  try {
    const stored = localStorage.getItem(SUPABASE_STORAGE_KEY);
    if (!stored) return false;
    
    const parsed = JSON.parse(stored);
    // Check if token exists and isn't expired
    if (parsed?.access_token && parsed?.expires_at) {
      // expires_at is in seconds, Date.now() is in milliseconds
      return parsed.expires_at * 1000 > Date.now();
    }
    return false;
  } catch {
    return false;
  }
};

/**
 * Store the intended destination before redirecting to auth
 * Only stores valid internal paths (starting with /) and excludes auth pages
 */
export const setReturnTo = (path: string): void => {
  if (path && path.startsWith('/') && !path.startsWith('/auth')) {
    sessionStorage.setItem(RETURN_TO_KEY, path);
  }
};

/**
 * Get return destination
 * Priority: URL param > sessionStorage
 * Returns null if no valid return path is found
 */
export const getReturnTo = (): string | null => {
  const params = new URLSearchParams(window.location.search);
  const urlReturnTo = params.get('returnTo');
  
  if (urlReturnTo && urlReturnTo.startsWith('/')) {
    return urlReturnTo;
  }
  
  return sessionStorage.getItem(RETURN_TO_KEY);
};

/**
 * Clear stored return path after use
 */
export const clearReturnTo = (): void => {
  sessionStorage.removeItem(RETURN_TO_KEY);
};

/**
 * Navigate to auth with returnTo preserved in both URL and sessionStorage.
 * Uses smart detection to show signin for returning visitors, signup for new.
 * This ensures the return path survives OAuth redirects.
 */
export const navigateToAuth = (navigate: NavigateFunction, currentPath?: string): void => {
  const pathToStore = currentPath || window.location.pathname + window.location.search;
  setReturnTo(pathToStore);
  
  navigate(getSmartAuthRoute(pathToStore), { replace: true });
};

/**
 * Get the post-login redirect path and clear the stored value
 * Defaults to /account if no return path is stored
 */
export const getPostLoginRedirectPath = (): string => {
  const returnTo = getReturnTo();
  clearReturnTo();
  return returnTo || ROUTES.ACCOUNT;
};

/**
 * Preserve locale and returnTo before OAuth redirect
 * Call this before initiating OAuth flows
 */
export const prepareOAuthRedirect = (): void => {
  const currentReturnTo = getReturnTo();
  if (currentReturnTo) {
    setReturnTo(currentReturnTo);
  }
  
  const savedLocale = localStorage.getItem('user-locale');
  if (savedLocale) {
    sessionStorage.setItem(OAUTH_LOCALE_KEY, savedLocale);
  }
};

/**
 * Get and clear stored OAuth locale after redirect
 */
export const consumeOAuthLocale = (): string | null => {
  const locale = sessionStorage.getItem(OAUTH_LOCALE_KEY);
  if (locale) {
    sessionStorage.removeItem(OAUTH_LOCALE_KEY);
  }
  return locale;
};

